import React, { useState, useEffect } from 'react';
import { Building2, Eye, EyeOff, X, Mail } from 'lucide-react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { auth } from '@/shared/config/firebase';
import { useAuth } from '@/app/providers';
import { toast } from 'react-hot-toast';

interface AuthModalProps {
  isOpen: boolean;
  isRegisterMode: boolean;
  onClose: () => void;
  onModeChange?: (isRegister: boolean) => void;
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, isRegisterMode, onClose, onModeChange }) => {
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState<string>('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [isResetting, setIsResetting] = useState(false);

  // Bloquer le scroll du body quand le modal est ouvert
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;

    setIsLoading(true);
    setAuthError('');

    try {
      if (isRegisterMode) {
        await register(email, password);
        // Message de succès pour l'inscription avec rappel de vérifier les spams
        toast.success(
          'Compte créé ! Un email de vérification a été envoyé. ⚠️ Vérifiez vos courriers indésirables (spam).',
          { duration: 8000 }
        );
      } else {
        await login(email, password);
      }
      onClose();
      setEmail('');
      setPassword('');
      setAuthError('');
      onModeChange?.(false);
      
      // Rediriger vers "Mes commandes" après connexion réussie et fermeture de la modal
      if (!isRegisterMode) {
        // Utiliser setTimeout pour s'assurer que la modal se ferme avant la navigation
        setTimeout(() => {
          navigate('/my-orders');
        }, 100);
      }
    } catch (error: any) {
      if (error?.code === 'auth/user-not-found') setAuthError('Aucun compte trouvé avec cet email.');
      else if (error?.code === 'auth/email-not-verified') setAuthError('Email non vérifié. Nous venons de renvoyer un lien de confirmation. ⚠️ Vérifiez vos courriers indésirables (spam).');
      else if (['auth/wrong-password', 'auth/invalid-credential', 'auth/invalid-login-credentials'].includes(error?.code)) setAuthError('Email ou mot de passe incorrect.');
      else if (error?.code === 'auth/email-already-in-use') setAuthError('Cet email est déjà utilisé.');
      else if (error?.code === 'auth/weak-password') setAuthError('Le mot de passe doit contenir au moins 6 caractères.');
      else if (error?.code === 'auth/invalid-email') setAuthError("Format d'email invalide.");
      else setAuthError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail) {
      toast.error('Veuillez entrer votre adresse email');
      return;
    }

    setIsResetting(true);

    try {
      await sendPasswordResetEmail(auth, resetEmail);
      toast.success('Email de réinitialisation envoyé ! Vérifiez votre boîte mail.');
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (error: any) {
      console.error('Erreur réinitialisation mot de passe:', error);
      if (error?.code === 'auth/user-not-found') {
        toast.error('Aucun compte trouvé avec cet email.');
      } else if (error?.code === 'auth/invalid-email') {
        toast.error("Format d'email invalide.");
      } else if (error?.code === 'auth/too-many-requests') {
        toast.error('Trop de tentatives. Veuillez réessayer plus tard.');
      } else {
        toast.error('Erreur lors de l\'envoi de l\'email. Veuillez réessayer.');
      }
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      style={{ 
        zIndex: 9999999,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh'
      }}
      onClick={onClose}
    >
      <div 
        className="bg-slate-900 rounded-2xl p-8 w-full max-w-md border border-slate-700 shadow-2xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl flex items-center justify-center">
              {showForgotPassword ? <Mail className="w-6 h-6 text-white" /> : <Building2 className="w-6 h-6 text-white" />}
            </div>
            <h2 className="text-2xl font-bold text-white">
              {showForgotPassword ? 'Mot de passe oublié' : isRegisterMode ? 'Créer un compte' : 'Se connecter'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-slate-700 transition-colors"
          >
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>

        {authError && !showForgotPassword && (
          <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl mb-6">
            <p className="text-red-400 text-sm font-medium">{authError}</p>
          </div>
        )}

        {showForgotPassword ? (
          /* Formulaire de réinitialisation du mot de passe */
          <>
            <div className="mb-6">
              <p className="text-slate-300 text-sm">
                Entrez votre adresse email et nous vous enverrons un lien pour réinitialiser votre mot de passe.
              </p>
            </div>

            <form onSubmit={handlePasswordReset} className="space-y-6">
              <div>
                <label htmlFor="reset-email" className="block text-sm font-medium text-slate-300 mb-2">
                  Email
                </label>
                <input
                  id="reset-email"
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:border-purple-500 transition-colors"
                  placeholder="votre@email.com"
                  autoComplete="email"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={isResetting}
                className="w-full px-6 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-500 hover:to-pink-500 transition-all font-semibold shadow-xl hover:shadow-purple-500/30 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isResetting ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Envoi en cours...</span>
                  </div>
                ) : (
                  'Envoyer le lien de réinitialisation'
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => setShowForgotPassword(false)}
                className="text-purple-400 hover:text-purple-300 font-semibold transition-colors"
              >
                ← Retour à la connexion
              </button>
            </div>
          </>
        ) : (
          /* Formulaire de connexion/inscription */
          <>
            <form onSubmit={handleAuth} className="space-y-6">
          <div>
            <label htmlFor="auth-email" className="block text-sm font-medium text-slate-300 mb-2">Email</label>
            <input
              id="auth-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:border-purple-500 transition-colors"
              placeholder="votre@email.com"
              autoComplete="email"
              required
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label htmlFor="auth-password" className="block text-sm font-medium text-slate-300">
                Mot de passe
              </label>
              {!isRegisterMode && (
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-purple-400 hover:text-purple-300 transition-colors"
                >
                  Mot de passe oublié ?
                </button>
              )}
            </div>
            <div className="relative">
              <input
                id="auth-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:border-purple-500 transition-colors pr-12"
                placeholder="••••••••"
                autoComplete={isRegisterMode ? 'new-password' : 'current-password'}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full px-6 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-500 hover:to-pink-500 transition-all font-semibold shadow-xl hover:shadow-purple-500/30 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {isLoading ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                <span>{isRegisterMode ? 'Création...' : 'Connexion...'}</span>
              </div>
            ) : (
              isRegisterMode ? 'Créer mon compte' : 'Se connecter'
            )}
          </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-slate-400">
                {isRegisterMode ? 'Déjà un compte ?' : 'Pas encore de compte ?'}{' '}
                <button
                  onClick={() => onModeChange?.(!isRegisterMode)}
                  className="text-purple-400 hover:text-purple-300 font-semibold transition-colors"
                >
                  {isRegisterMode ? 'Se connecter' : 'Créer un compte'}
                </button>
              </p>
            </div>

            <div className="mt-6 p-4 bg-slate-800/50 rounded-xl border border-slate-700">
              <p className="text-sm text-slate-300 text-center">
                En vous connectant, vous acceptez nos conditions d'utilisation et notre politique de confidentialité.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthModal;







